import { useCallback } from 'react';
import { Game } from '../games.model';
import { GameGrid } from '../GameGrid';
import { SectionConfig } from '../GameCard/GameCard.types';
import { TileRowCollection as TileRowCollectionType } from '../GameCard/GameCard.types';
import { Grid, GridItem } from '../../shared/Grid';
import { getRequiredGameCount } from '../../../utils/games/createTileRowCollections';

interface MobileSlideCreatorProps {
  collectionsWithContent: TileRowCollectionType[];
  findGameById: (id: string) => Game | undefined;
  containerWidth: number;
  disabled: boolean;
  showGameName: boolean;
  fallbackImageAltText: string;
  fallbackImageUrl: string;
  onViewGameInfo?: (game: Game) => void;
  onPlayDemo?: (game: Game) => void;
  onPlayGame?: (game: Game) => void;
}

export const useMobileSlideCreator = ({
  collectionsWithContent,
  findGameById,
  containerWidth,
  disabled,
  showGameName,
  fallbackImageAltText,
  fallbackImageUrl,
  onViewGameInfo,
  onPlayDemo,
  onPlayGame,
}: MobileSlideCreatorProps): { createMobileSlides: () => JSX.Element[] } => {
  const createMobileSlides = useCallback((): JSX.Element[] => {
    if (collectionsWithContent.length === 0) return [];

    const mobileSlides: JSX.Element[] = [];

    for (let i = 0; i < collectionsWithContent.length; i++) {
      const collection = collectionsWithContent[i];

      const sections = [
        collection.first_full_width_tile,
        collection.second_full_width_tile,
        collection.third_full_width_tile_block,
        ...(collection.half_width_tile ? [collection.half_width_tile] : []),
      ].filter(Boolean) as SectionConfig[];

      const pairings =
        sections.length === 3
          ? [
              [0, 1], // first_full_width_tile + second_full_width_tile
              [2, 0], // third_full_width_tile_block + first_full_width_tile
              [1, 2], // second_full_width_tile + third_full_width_tile_block
            ]
          : sections.length === 2
            ? [[0], [1]] // Treat each section as its own slide when two sections are present
            : sections.length === 1
              ? [[0]] // half_width_tile (single section)
              : [];

      for (let j = 0; j < pairings.length; j++) {
        const pairing = pairings[j];
        const [firstIndex, secondIndex] = pairing;

        if (firstIndex >= sections.length || firstIndex < 0) continue;

        if (pairing.length > 1 && (secondIndex >= sections.length || secondIndex < 0)) continue;

        const firstSection = sections[firstIndex];
        const secondSection = pairing.length > 1 ? sections[secondIndex] : null;

        if (!firstSection) continue;

        const firstSectionGames =
          (firstSection.games?.map((gameId) => findGameById(gameId)).filter(Boolean) as Game[]) || [];

        const secondSectionGames = secondSection
          ? (secondSection.games?.map((gameId) => findGameById(gameId)).filter(Boolean) as Game[]) || []
          : [];

        const firstSectionRequiredCount = getRequiredGameCount(firstSection.type);
        const secondSectionRequiredCount = secondSection ? getRequiredGameCount(secondSection.type) : 0;

        if (firstSectionGames.length < firstSectionRequiredCount) {
          continue;
        }

        if (secondSection && secondSectionGames.length < secondSectionRequiredCount) {
          continue;
        }

        const mobileSlide = (
          <div key={`mobile-slide-${i}-${j}`} className="mobile-carousel-slide">
            <Grid columns={1} gap="s" className="mobile-slide-grid">
              <GridItem>
                <GameGrid
                  sectionType={firstSection.type}
                  games={firstSectionGames}
                  disabled={disabled}
                  showGameName={showGameName}
                  fallbackImageAltText={fallbackImageAltText}
                  fallbackImageUrl={fallbackImageUrl}
                  onViewGameInfo={onViewGameInfo}
                  onPlayDemo={onPlayDemo}
                  onPlayGame={onPlayGame}
                  containerElementWidth={containerWidth}
                />
              </GridItem>
              {secondSection && (
                <GridItem>
                  <GameGrid
                    sectionType={secondSection.type}
                    games={secondSectionGames}
                    disabled={disabled}
                    showGameName={showGameName}
                    fallbackImageAltText={fallbackImageAltText}
                    fallbackImageUrl={fallbackImageUrl}
                    onViewGameInfo={onViewGameInfo}
                    onPlayDemo={onPlayDemo}
                    onPlayGame={onPlayGame}
                    containerElementWidth={containerWidth}
                  />
                </GridItem>
              )}
            </Grid>
          </div>
        );

        mobileSlides.push(mobileSlide);
      }
    }

    return mobileSlides;
  }, [
    collectionsWithContent,
    findGameById,
    containerWidth,
    disabled,
    showGameName,
    fallbackImageAltText,
    fallbackImageUrl,
    onViewGameInfo,
    onPlayDemo,
    onPlayGame,
  ]);

  return { createMobileSlides };
};
